import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Container,
  useMediaQuery,
  useTheme,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
} from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  Instagram as InstagramIcon,
  QrCode as QrCodeIcon,
  CreditCard as CardIcon,
  Label as StickerIcon,
  Send as SendIcon,
  Description as MenuIcon,
  Image as ImageIcon,
  MyLocation as MyLocationIcon,
  Clear as ClearIcon,
  CurrencyRupee as CurrencyIcon,
} from '@mui/icons-material';

const RestaurantOrderPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    fssai: '',
    restMobile: '',
    ownerMobile: '',
    email: '',
    address: '',
    instagram: '',
    tableCount: '',
    cardCount: '',
    stickerCount: '',
  });
  const [errors, setErrors] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    // Clear toast when user starts typing in any field
    if (toast.open) {
      setToast({ open: false, message: '' });
    }
    // Clear requirements error when any requirement field is filled
    if ((name === 'tableCount' || name === 'cardCount' || name === 'stickerCount') && value && parseInt(value) > 0) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.orderRequirements;
        return newErrors;
      });
    }
  };

  const handleClear = () => {
    setFormData({
      name: '',
      location: '',
      fssai: '',
      restMobile: '',
      ownerMobile: '',
      email: '',
      address: '',
      instagram: '',
      tableCount: '',
      cardCount: '',
      stickerCount: '',
    });
    setErrors({});
    setToast({ open: false, message: '' });
    setShowAlert(false);
    setConfirmDialogOpen(false);
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setErrors((prev) => ({
        ...prev,
        location: 'Geolocation is not supported by your browser',
      }));
      return;
    }

    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setFormData((prev) => ({
          ...prev,
          location: `${latitude}, ${longitude}`,
        }));
        setLocationLoading(false);
        if (errors.location) {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.location;
            return newErrors;
          });
        }
      },
      (error) => {
        setLocationLoading(false);
        setErrors((prev) => ({
          ...prev,
          location: 'Unable to retrieve your location. Please enter coordinates manually.',
        }));
      }
    );
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Check validations and show toast for first error
    let validationToastShown = false;
    
    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Restaurant name is required';
      if (!validationToastShown) {
        setToast({ open: true, message: 'Restaurant name is required' });
        validationToastShown = true;
      }
    }
    
    if (!formData.fssai || !formData.fssai.trim()) {
      newErrors.fssai = 'FSSAI number is required';
      if (!validationToastShown) {
        setToast({ open: true, message: 'FSSAI number is required' });
        validationToastShown = true;
      }
    } else {
      const fssaiPattern = /^\d{14}$/;
      if (!fssaiPattern.test(formData.fssai.trim().replace(/\s/g, ''))) {
        newErrors.fssai = 'FSSAI number must be exactly 14 digits';
        if (!validationToastShown) {
          setToast({ open: true, message: 'FSSAI number must be exactly 14 digits' });
          validationToastShown = true;
        }
      }
    }
    
    if (!formData.restMobile || !formData.restMobile.trim()) {
      newErrors.restMobile = 'Restaurant mobile is required';
      if (!validationToastShown) {
        setToast({ open: true, message: 'Restaurant mobile is required' });
        validationToastShown = true;
      }
    } else {
      const cleanedMobile = formData.restMobile.trim().replace(/\D/g, '');
      if (cleanedMobile.length !== 10) {
        newErrors.restMobile = 'Mobile number must be exactly 10 digits';
        if (!validationToastShown) {
          setToast({ open: true, message: 'Restaurant mobile number must be exactly 10 digits' });
          validationToastShown = true;
        }
      }
    }
    
    if (!formData.email || !formData.email.trim()) {
      newErrors.email = 'Email is required';
      if (!validationToastShown) {
        setToast({ open: true, message: 'Email is required' });
        validationToastShown = true;
      }
    } else {
      const trimmedEmail = formData.email.trim();
      // More comprehensive email validation pattern
      const emailPattern = /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,}$/;
      if (!emailPattern.test(trimmedEmail)) {
        newErrors.email = 'Please enter a valid email address (e.g., example@domain.com)';
        if (!validationToastShown) {
          setToast({ open: true, message: 'Please enter a valid email address (e.g., example@domain.com)' });
          validationToastShown = true;
        }
      }
    }
    
    if (!formData.ownerMobile || !formData.ownerMobile.trim()) {
      newErrors.ownerMobile = 'Owner/Manager mobile is required';
      if (!validationToastShown) {
        setToast({ open: true, message: 'Owner/Manager mobile is required' });
        validationToastShown = true;
      }
    } else {
      const cleanedMobile = formData.ownerMobile.trim().replace(/\D/g, '');
      if (cleanedMobile.length !== 10) {
        newErrors.ownerMobile = 'Mobile number must be exactly 10 digits';
        if (!validationToastShown) {
          setToast({ open: true, message: 'Owner/Manager mobile number must be exactly 10 digits' });
          validationToastShown = true;
        }
      }
    }
    
    if (!formData.address || !formData.address.trim()) {
      newErrors.address = 'Address is required';
      if (!validationToastShown) {
        setToast({ open: true, message: 'Address is required' });
        validationToastShown = true;
      }
    }
    
    // Menu and Logo are always required (no checkbox needed)
    
    // At least one requirement must be filled
    const hasTableCount = formData.tableCount && parseInt(formData.tableCount) > 0;
    const hasCardCount = formData.cardCount && parseInt(formData.cardCount) > 0;
    const hasStickerCount = formData.stickerCount && parseInt(formData.stickerCount) > 0;
    
    if (!hasTableCount && !hasCardCount && !hasStickerCount) {
      newErrors.orderRequirements = 'At least one requirement (Table Count, Visiting Cards, or Sticker Count) is required';
      if (!validationToastShown) {
        setToast({ open: true, message: 'At least one requirement (Table Count, Visiting Cards, or Sticker Count) is required' });
        validationToastShown = true;
      }
    }
    

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFilesToShare = () => {
    const files = [
      `Menu PDF/Image for: ${formData.name}`,
      `Logo Image for: ${formData.name}`,
    ];
    
    if (formData.cardCount && parseInt(formData.cardCount) > 0) {
      files.push(`Visiting Card Image for: ${formData.name}`);
    }
    
    return files;
  };

  const handleSendClick = () => {
    if (!validateForm()) {
      return;
    }
    setConfirmDialogOpen(true);
  };

  const calculateCost = () => {
    const standCount = parseInt(formData.tableCount) || 0;
    const stickerQty = parseInt(formData.stickerCount) || 0;
    const cardQty = parseInt(formData.cardCount) || 0;

    // Stand cost: ₹85 per stand
    const standCost = standCount * 85;

    // Stickers: 9 per sheet, ₹50 per sheet
    const stickerSheets = Math.ceil(stickerQty / 9);
    const stickerCost = stickerQty > 0 ? stickerSheets * 50 : 0;
    const stickerRemain = stickerQty > 0 && stickerQty % 9 !== 0 ? 9 - (stickerQty % 9) : 0;

    // Visiting Cards: 30 per sheet, ₹20 per sheet
    const cardSheets = Math.ceil(cardQty / 30);
    const cardCost = cardQty > 0 ? cardSheets * 20 : 0;
    const cardRemain = cardQty > 0 && cardQty % 30 !== 0 ? 30 - (cardQty % 30) : 0;

    // Fixed cutting cost
    const cuttingCost = 85;

    const total = standCost + stickerCost + cardCost + cuttingCost;

    return {
      standCount,
      standCost,
      stickerQty,
      stickerSheets,
      stickerCost,
      stickerRemain,
      cardQty,
      cardSheets,
      cardCost,
      cardRemain,
      cuttingCost,
      total,
    };
  };

  const sendWhatsApp = () => {
    const fileRequirements = getFilesToShare();
    const fileNote = `\n\n*Files to Share:*\n${fileRequirements.map(f => `• ${f}`).join('\n')}`;

    const cost = calculateCost();
    
    // Build cost breakdown
    let costBreakdown = '';
    if (cost.total > 0) {
      costBreakdown = '\n\n*💰 Cost Breakdown:*\n';
      
      if (cost.standCount > 0) {
        costBreakdown += `🔲 Stand Cost: ₹${cost.standCost} (${cost.standCount} × ₹85)\n`;
      }
      
      if (cost.stickerQty > 0) {
        costBreakdown += `🏷️ Sticker Cost: ₹${cost.stickerCost} (${cost.stickerSheets} sheet${cost.stickerSheets > 1 ? 's' : ''} × ₹50)\n`;
        if (cost.stickerRemain > 0) {
          costBreakdown += `   💡 Add ${cost.stickerRemain} more sticker(s) to complete a full sheet\n`;
        }
      }
      
      if (cost.cardQty > 0) {
        costBreakdown += `💳 Visiting Card Cost: ₹${cost.cardCost} (${cost.cardSheets} sheet${cost.cardSheets > 1 ? 's' : ''} × ₹20)\n`;
        if (cost.cardRemain > 0) {
          costBreakdown += `   💡 Add ${cost.cardRemain} more card(s) to complete a full sheet\n`;
        }
      }
      
      costBreakdown += `✂️ Cutting Cost: ₹${cost.cuttingCost} (Fixed)\n`;
      costBreakdown += `\n💵 *Total Cost: ₹${cost.total}*`;
    }

    const message = `🍽️ *Restaurant Onboarding Details*

*Restaurant Information:*
🏪 Restaurant Name: ${formData.name || 'Not provided'}
📍 Location: ${formData.location || 'Not provided'}
📋 FSSAI Number: ${formData.fssai || 'Not provided'}

*Contact Information:*
📱 Restaurant Mobile: ${formData.restMobile || 'Not provided'}
👤 Owner/Manager Mobile: ${formData.ownerMobile || 'Not provided'}
📧 Email: ${formData.email || 'Not provided'}

*Address:*
${formData.address || 'Not provided'}

*Social Media:*
📸 Instagram ID: ${formData.instagram || 'Not provided'}

*Requirements:*
🔲 QR Stand for Tables: ${formData.tableCount || 'Not Required'}
💳 Visiting Cards Required: ${formData.cardCount || 'Not Required'}
🏷️ QR Stickers (Direct on Table): ${formData.stickerCount || 'Not Required'}${costBreakdown}${fileNote}`;

    const phoneNumber = '917907147438'; // WhatsApp number
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    setConfirmDialogOpen(false);
    window.open(whatsappUrl, '_blank');
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 4 },
          borderRadius: 2,
        }}
      >
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <RestaurantIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: 'primary.main', mb: 1 }} />
          <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" gutterBottom>
            Restaurant Onboarding
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fill in the details below to onboard your restaurant
          </Typography>
        </Box>

        {showAlert && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setShowAlert(false)}>
            WhatsApp message prepared! The form will open in a new window.
          </Alert>
        )}

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {/* Restaurant Information Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RestaurantIcon color="primary" />
              Restaurant Information
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Restaurant Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Location (Coordinates or Address)"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., 28.6139, 77.2090 or Enter address"
              error={!!errors.location}
              helperText={errors.location || 'Enter coordinates (lat, lng) or click button to get current location'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={getCurrentLocation}
                      disabled={locationLoading}
                      startIcon={<MyLocationIcon />}
                      sx={{ minWidth: 'auto', mr: -1 }}
                    >
                      {locationLoading ? 'Getting...' : 'Get Location'}
                    </Button>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="FSSAI Number"
              name="fssai"
              value={formData.fssai}
              onChange={(e) => {
                // Only allow digits
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 14) {
                  setFormData((prev) => ({
                    ...prev,
                    fssai: value,
                  }));
                  // Clear error when user starts typing
                  if (errors.fssai) {
                    setErrors((prev) => ({
                      ...prev,
                      fssai: '',
                    }));
                  }
                }
              }}
              placeholder="14-digit FSSAI number"
              error={!!errors.fssai}
              helperText={errors.fssai || 'Must be exactly 14 digits'}
              inputProps={{ maxLength: 14 }}
              variant="outlined"
            />
          </Grid>

          {/* Contact Information Section */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon color="primary" />
              Contact Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Restaurant Mobile"
              name="restMobile"
              type="tel"
              value={formData.restMobile}
              onChange={(e) => {
                // Only allow digits, max 10
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 10) {
                  setFormData((prev) => ({
                    ...prev,
                    restMobile: value,
                  }));
                  // Clear error and toast when user starts typing
                  if (errors.restMobile) {
                    setErrors((prev) => ({
                      ...prev,
                      restMobile: '',
                    }));
                  }
                  if (toast.open) {
                    setToast({ open: false, message: '' });
                  }
                }
              }}
              placeholder="10-digit mobile number"
              error={!!errors.restMobile}
              helperText={errors.restMobile || 'Must be exactly 10 digits'}
              inputProps={{ maxLength: 10 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Owner / Manager Mobile"
              name="ownerMobile"
              type="tel"
              value={formData.ownerMobile}
              onChange={(e) => {
                // Only allow digits, max 10
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 10) {
                  setFormData((prev) => ({
                    ...prev,
                    ownerMobile: value,
                  }));
                  // Clear error and toast when user starts typing
                  if (errors.ownerMobile) {
                    setErrors((prev) => ({
                      ...prev,
                      ownerMobile: '',
                    }));
                  }
                  if (toast.open) {
                    setToast({ open: false, message: '' });
                  }
                }
              }}
              placeholder="10-digit mobile number"
              error={!!errors.ownerMobile}
              helperText={errors.ownerMobile || 'Must be exactly 10 digits. Used for managing menu and payments. Can be same as restaurant mobile.'}
              inputProps={{ maxLength: 10 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Restaurant Email (or Owner Email)"
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => {
                const value = e.target.value.trim();
                setFormData((prev) => ({
                  ...prev,
                  email: e.target.value, // Keep original for typing, will trim on validation
                }));
                // Clear error and toast when user starts typing
                if (errors.email) {
                  setErrors((prev) => ({
                    ...prev,
                    email: '',
                  }));
                }
                if (toast.open) {
                  setToast({ open: false, message: '' });
                }
              }}
              onBlur={(e) => {
                // Trim on blur
                const trimmedValue = e.target.value.trim();
                if (trimmedValue !== formData.email) {
                  setFormData((prev) => ({
                    ...prev,
                    email: trimmedValue,
                  }));
                }
              }}
              placeholder="example@domain.com"
              error={!!errors.email}
              helperText={errors.email || 'Enter a valid email address'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Instagram ID"
              name="instagram"
              value={formData.instagram}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <InstagramIcon color="action" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Restaurant Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              error={!!errors.address}
              helperText={errors.address}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <HomeIcon color="action" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>


          {/* Requirements Section */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QrCodeIcon color="primary" />
              Requirements
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              At least one of the following is required:
            </Typography>
            {errors.orderRequirements && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                {errors.orderRequirements}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="QR Stand for Tables – Table Count"
              name="tableCount"
              type="number"
              value={formData.tableCount}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <QrCodeIcon color="action" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ min: 0 }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Visiting Cards Required – Count"
              name="cardCount"
              type="number"
              value={formData.cardCount}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CardIcon color="action" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ min: 0 }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="QR Stickers (Direct on Table)"
              name="stickerCount"
              type="number"
              helperText="Number of tables to receive QR stickers directly (without stand)"
              value={formData.stickerCount}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <StickerIcon color="action" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ min: 0 }}
              variant="outlined"
            />
          </Grid>

          {/* Cost Breakdown Section */}
          {(() => {
            const cost = calculateCost();
            if (cost.total > 0) {
              return (
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CurrencyIcon color="primary" />
                      Cost Breakdown
                    </Typography>

                    {cost.standCount > 0 && (
                      <Box sx={{ mt: 2, mb: 1.5 }}>
                        <Typography variant="body1" fontWeight="medium">
                          Stand Cost: ₹{cost.standCost}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cost.standCount} stand(s) × ₹85
                        </Typography>
                      </Box>
                    )}

                    {cost.stickerQty > 0 && (
                      <Box sx={{ mt: 2, mb: 1.5 }}>
                        <Typography variant="body1" fontWeight="medium">
                          Sticker Cost ({cost.stickerSheets} sheet{cost.stickerSheets > 1 ? 's' : ''}): ₹{cost.stickerCost}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {cost.stickerQty} sticker(s) × {cost.stickerSheets} sheet(s) × ₹50
                        </Typography>
                        {cost.stickerRemain > 0 ? (
                          <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                            💡 Add {cost.stickerRemain} more sticker(s) to complete a full sheet
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                            ✔ Full sticker sheet covered
                          </Typography>
                        )}
                      </Box>
                    )}

                    {cost.cardQty > 0 && (
                      <Box sx={{ mt: 2, mb: 1.5 }}>
                        <Typography variant="body1" fontWeight="medium">
                          Visiting Card Cost ({cost.cardSheets} sheet{cost.cardSheets > 1 ? 's' : ''}): ₹{cost.cardCost}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {cost.cardQty} card(s) × {cost.cardSheets} sheet(s) × ₹20
                        </Typography>
                        {cost.cardRemain > 0 ? (
                          <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                            💡 Add {cost.cardRemain} more card(s) to complete a full sheet
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                            ✔ Full visiting card sheet covered
                          </Typography>
                        )}
                      </Box>
                    )}

                    <Box sx={{ mt: 2, mb: 1.5 }}>
                      <Typography variant="body1" fontWeight="medium">
                        Cutting Cost: ₹{cost.cuttingCost}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Fixed cost
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        mt: 3,
                        pt: 2,
                        borderTop: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold" color="primary.main">
                        Total Cost: ₹{cost.total}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              );
            }
            return null;
          })()}

          {/* Submit Button */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleClear}
                startIcon={<ClearIcon />}
                sx={{
                  py: 1.5,
                }}
              >
                Clear
              </Button>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSendClick}
                startIcon={<SendIcon />}
                sx={{
                  py: 1.5,
                  backgroundColor: '#25D366',
                  '&:hover': {
                    backgroundColor: '#20BA5A',
                  },
                }}
              >
                Send to WhatsApp
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              The message will open in WhatsApp. Please attach files manually if needed.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SendIcon color="primary" />
            Confirm Send to WhatsApp
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please make sure to attach the following files when sending the message:
          </DialogContentText>
          <List dense>
            {getFilesToShare().map((file, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  {index === 0 ? (
                    <MenuIcon color="primary" />
                  ) : index === 1 ? (
                    <ImageIcon color="primary" />
                  ) : (
                    <ImageIcon color="primary" />
                  )}
                </ListItemIcon>
                <ListItemText primary={file} />
              </ListItem>
            ))}
          </List>
          <DialogContentText sx={{ mt: 2, fontSize: '0.875rem' }}>
            The WhatsApp message will open in a new window. Please attach these files manually.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={sendWhatsApp}
            variant="contained"
            startIcon={<SendIcon />}
            sx={{
              backgroundColor: '#25D366',
              '&:hover': {
                backgroundColor: '#20BA5A',
              },
            }}
          >
            Send to WhatsApp
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification for Mobile Validation */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast({ open: false, message: '' })}
          severity="error"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RestaurantOrderPage;
